import { useState } from 'react';
import User from '../../../../../models/User';
import AllSubscriptions from 'src/models/AllSubscriptions';
import { reactivateSubscription } from 'src/services/reactivation.service';
import { AxiosError } from 'axios';
import {
  trackReactivationCompleted,
  trackReactivationFailed,
  trackReactivationSubmitted,
} from 'src/util/track';

export const useReactivateSubscription = (
  user: User | undefined,
  allSubscriptions: AllSubscriptions | undefined,
  options?: {
    onSuccess?: () => Promise<void> | void;
  }
) => {
  const [reactivatePopupVisible, setReactivatePopupVisible] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [reactivateError, setReactivateError] = useState('');

  // Reactivate subscription is allowed if:
  // - User has an active subscription, trial subscription, or upcoming subscription
  // - User's payment provider is Stripe
  // - User has a cancellation date
  const isReactivateAllowed =
    (Boolean(allSubscriptions?.currentSubscription) ||
      Boolean(allSubscriptions?.trialSubscription) ||
      Boolean(allSubscriptions?.upcomingSubscription)) &&
    user?.payment_provider === 'Stripe' &&
    Boolean(user?.cancellationDate);

  const handleReactivateConfirm = async () => {
    if (!user) return;
    setIsReactivating(true);
    setReactivateError('');
    trackReactivationSubmitted(
      'update_subscription',
      'stripe',
      allSubscriptions?.currentSubscription?.planName,
      undefined,
      'cancel_reactivate'
    );
    try {
      await reactivateSubscription({
        userId: user.id,
        email: user.email,
      });
      trackReactivationCompleted(
        'update_subscription',
        'stripe',
        allSubscriptions?.currentSubscription?.planName,
        undefined,
        'cancel_reactivate'
      );
      if (options?.onSuccess) {
        await options.onSuccess();
      } else {
        window.location.reload();
      }
    } catch (err) {
      const errorMsg =
        (err as unknown as AxiosError)?.response?.data?.message ||
        'Failed to reactivate subscription. Please try again.';
      trackReactivationFailed(
        'update_subscription',
        'stripe',
        errorMsg,
        allSubscriptions?.currentSubscription?.planName,
        'cancel_reactivate'
      );
      setIsReactivating(false);
      setReactivateError(errorMsg);
    }
  };

  return {
    isReactivateAllowed,
    reactivatePopupVisible,
    setReactivatePopupVisible,
    isReactivating,
    reactivateError,
    handleReactivateConfirm,
  };
};
